import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import "./App.css";
import LoginPage from "./components/LoginPage";
import Dashboard from "./components/Dashboard";
import Today from "./components/Today";
import ScheduleManager from "./components/ScheduleManager";
import ShiftTemplates from "./components/ShiftTemplates";
import PTOManagement from "./components/PTOManagement";
import Reports from "./components/Reports";
import Admin from "./components/Admin";
import UserProfile from "./components/UserProfile";
import DebugSchedule from "./components/DebugSchedule";
import ShiftSwaps from "./components/ShiftSwaps";
import HelpPage from "./components/HelpPage";
import ScheduleInsights from "./components/ScheduleInsights";
import AuditLog from "./components/AuditLog";
import TimeTracking from "./components/TimeTracking";
import MySchedule from "./components/MySchedule";
import PlatformAdminPage from "./components/PlatformAdminPage";
import AcceptInvite from "./components/AcceptInvite";
import TeamInvites from "./components/TeamInvites";
import Directory from "./components/Directory";
import { SessionTimeout } from "./components/SessionTimeout";
import Sidebar from "./components/Sidebar";
import { Toaster } from "./components/ui/sonner";
import { checkAuthValidity, setupAuthInterceptor, setAuthToken as saveAuthToken, clearAuth } from "./utils/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Setup global auth interceptor to handle 401 errors
setupAuthInterceptor(axios);

// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState(() => {
    try {
      const stored = localStorage.getItem('permissions');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [sidebarAccess, setSidebarAccess] = useState(() => {
    try {
      const stored = localStorage.getItem('sidebarAccess');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [currentOrg, setCurrentOrg] = useState(() => {
    try {
      const stored = localStorage.getItem('currentOrg');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const fetchPermissions = async (authToken) => {
    try {
      const res = await axios.get(`${API}/auth/me/permissions`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const perms = res.data?.permissions || [];
      setPermissions(perms);
      localStorage.setItem('permissions', JSON.stringify(perms));
      const sa = res.data?.sidebar_access ?? null;
      setSidebarAccess(sa);
      localStorage.setItem('sidebarAccess', JSON.stringify(sa));
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
    }
  };

  const hasPermission = (perm) => {
    if (!permissions || permissions.length === 0) return false;
    return permissions.includes(perm);
  };

  // Check auth validity on mount and periodically
  useEffect(() => {
    // Check on mount
    if (token && !checkAuthValidity()) {
      console.log('Token expired on mount - clearing auth');
      setUser(null);
      setToken(null);
      return;
    }

    // Check every 5 minutes
    const interval = setInterval(() => {
      if (token && !checkAuthValidity()) {
        console.log('Token expired - auto logout');
        setUser(null);
        setToken(null);
        clearAuth();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      console.log('Setting axios authorization header with token:', token.substring(0, 20) + '...');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Refresh permissions on mount if we have a token but no cached permissions
      if (!permissions || permissions.length === 0) {
        fetchPermissions(token);
      }
    } else {
      console.log('No token found in localStorage');
    }
  }, [token]);

  const login = (userData, authToken, orgData = null) => {
    console.log('Login called with:', userData);
    setUser(userData);
    setToken(authToken);
    saveAuthToken(authToken);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    if (orgData) {
      setCurrentOrg(orgData);
      localStorage.setItem('currentOrg', JSON.stringify(orgData));
    }
    fetchPermissions(authToken);
  };

  const logout = async () => {
    // Clear the httpOnly cookie server-side (best-effort).
    try { await axios.post(`${API}/auth/logout`); } catch (e) { /* ignore */ }
    setUser(null);
    setToken(null);
    setPermissions([]);
    setSidebarAccess(null);
    setCurrentOrg(null);
    clearAuth();
    localStorage.removeItem('permissions');
    localStorage.removeItem('sidebarAccess');
    localStorage.removeItem('currentOrg');
    delete axios.defaults.headers.common['Authorization'];
    window.location.href = '/';
  };

  const switchOrg = async (orgId) => {
    try {
      const res = await axios.post(`${API}/platform/orgs/${orgId}/switch`);
      const { token: newToken, org_id, user: orgUser } = res.data;
      setToken(newToken);
      setUser(orgUser);
      saveAuthToken(newToken);
      localStorage.setItem('user', JSON.stringify(orgUser));
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      const orgsRes = await axios.get(`${API}/platform/orgs`, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      const activeOrg = (orgsRes.data || []).find(o => o.org_id === org_id);
      if (activeOrg) {
        setCurrentOrg(activeOrg);
        localStorage.setItem('currentOrg', JSON.stringify(activeOrg));
      }
      await fetchPermissions(newToken);
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch org:', err);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, permissions, hasPermission, sidebarAccess, currentOrg, switchOrg }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" replace />;
};

const AppLayout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [featureToggles, setFeatureToggles] = React.useState({});
  const { token } = useAuth();

  React.useEffect(() => {
    if (!token) return;
    axios.get(`${API}/feature-toggles`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setFeatureToggles(res.data || {}))
      .catch(() => {});
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <SessionTimeout />
      <Sidebar onToggle={setSidebarCollapsed} featureToggles={featureToggles} />
      <main className={`flex-1 min-w-0 transition-all duration-300 ease-in-out ${
        sidebarCollapsed ? 'ml-16' : 'ml-64'
      }`}>
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/platform-login" element={<PlatformAdminPage />} />
            <Route path="/platform-admin" element={<PlatformAdminPage />} />
            <Route path="/super-admin" element={<PlatformAdminPage />} />
            <Route path="/admin-panel" element={<PlatformAdminPage />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/debug-schedule" element={<AppLayout><ScheduleManager /></AppLayout>} />
            <Route path="/simple-test" element={<DebugSchedule />} />
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/today" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Today />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/my-schedule" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <MySchedule />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/schedule" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ScheduleManager />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/shift-templates" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ShiftTemplates />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/pto" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <PTOManagement />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/shift-swaps" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ShiftSwaps />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/schedule-insights" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ScheduleInsights />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/audit-log" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AuditLog />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/reports" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Reports />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Admin />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <UserProfile />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/help" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <HelpPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/team" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <TeamInvites />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/time-tracking" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <TimeTracking />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/directory" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Directory />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route path="/ai" element={<Navigate to="/schedule-insights" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthProvider>
  );
}

export default App;